import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Table } from 'antd'

import getReimbursementsBalances, {
  PagedReimbursementsBalances,
  ReimbursementBalanceItem,
} from 'core/application/adminDashboard/reimbursements/reimbursementsBalances/getReimbursementsBalances'
import getHraBenefitOverview, { HraBenefitOverviewResponse } from 'core/application/benefit/getHraBenefitOverview'
import OrderedPageFilter from 'core/application/commons/orderedPageFilter'

import { queryRequest } from 'common/RequestUtils'

import { reimbursementsBalancesColumns } from './ReimbursementsBalancesColumns'
import BalanceDetails from './balanceDetails'
import CompanyTotalBalance from './companyTotalBalance'

const pagedColumnDefaultValues = {
  items: [],
  pageCount: 1,
  pageNumber: 1,
  pageSize: 10,
  totalItemCount: 0,
}

const defaultFilter = {
  pageNumber: 1,
  pageSize: 10,
  orderColumn: 'fullName',
  orderDescending: false,
}

interface ReimbursementsBalancesProps {
  planPeriodId: string
}

const ReimbursementsBalances: React.FC<ReimbursementsBalancesProps> = ({ planPeriodId }) => {
  const [loadingBalances, setLoadingBalances] = useState<boolean>(true)
  const [reimbursementsBalances, setReimbursementsBalances] =
    useState<PagedReimbursementsBalances>(pagedColumnDefaultValues)
  const [reimbursementsBalancesFilter, setReimbursementsBalancesFilter] = useState<OrderedPageFilter>(defaultFilter)
  const [allowanceSummary, setAllowanceSummary] = useState<HraBenefitOverviewResponse>()
  const { t: translate } = useTranslation()

  useEffect(() => {
    getReimbursements()
    getAllowanceSummary()
  }, [planPeriodId])

  useEffect(() => {
    getReimbursements()
  }, [reimbursementsBalancesFilter])

  const getReimbursements = async () => {
    setLoadingBalances(true)
    const result = await queryRequest(() => getReimbursementsBalances(reimbursementsBalancesFilter, planPeriodId))
    setReimbursementsBalances(result)

    setLoadingBalances(false)
  }

  const getAllowanceSummary = async () => {
    const allowancesReport = await queryRequest(() => getHraBenefitOverview(planPeriodId))
    setAllowanceSummary(allowancesReport)
  }

  const onReimbursementsBalancesTableChange = (pagination: any, filters: any, sorter: any) => {
    setReimbursementsBalancesFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: 'fullName',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <div className="mt-24">
      <Table
        className={'t-reimbursement-balances'}
        dataSource={reimbursementsBalances.items}
        onChange={onReimbursementsBalancesTableChange}
        loading={loadingBalances}
        scroll={{ x: 1000 }}
        columns={reimbursementsBalancesColumns(translate, reimbursementsBalancesFilter)}
        tableLayout="fixed"
        pagination={{
          total: reimbursementsBalances.totalItemCount,
          pageSize: reimbursementsBalances.pageSize,
          current: reimbursementsBalances.pageNumber,
        }}
        rowKey="companyUserId"
        expandedRowRender={(balance: ReimbursementBalanceItem) => {
          return <BalanceDetails companyUserId={balance.companyUserId} planPeriodId={planPeriodId} />
        }}
        summary={() => (
          <CompanyTotalBalance
            translate={translate}
            allowanceYtd={allowanceSummary?.totalAllowance ?? 0}
            reimbursementsYtd={allowanceSummary?.totalReimbursed ?? 0}
            availableAllowance={allowanceSummary?.totalAllowanceRemaining ?? 0}
          />
        )}
      />
    </div>
  )
}

export default ReimbursementsBalances
